// Pure maths for the draggable stepper (components/ui/draggable-stepper.js).
//
// The middle of the stepper is a sideways strip of values, one step apart in
// both value and pixels, so the number under the finger is the one you get.
// The strip follows the finger: dragging right slides the smaller values into
// the centre, as on a wheel picker. A flick keeps it gliding for long ranges.

/** Clamp to [min, max] and snap to the step grid that starts at `min`. */
export const snapToStep = (value, { min, max, step }) => {
  const snapped = min + Math.round((value - min) / step) * step;
  // Tidy floating-point steps such as 2.5.
  const tidy = Math.round(snapped * 1000) / 1000;
  return Math.min(max, Math.max(min, tidy));
};

/**
 * A typed value, kept as typed: only clamped to [min, max] and rounded
 * to a sensible precision, whole numbers when the step is whole and
 * hundredths otherwise. The step grid is for the buttons and the strip, so
 * 96 kg on a 2.5 kg stepper stays 96.
 */
export const tidyTyped = (value, { min, max, step }) => {
  const places = Number.isInteger(step) ? 1 : 100;
  const tidy = Math.round(value * places) / places;
  return Math.min(max, Math.max(min, tidy));
};

/**
 * One press of − or + from `value`. On the grid that is a whole step; off it
 * (a typed 96 on a 2.5 grid) it goes to the nearest grid value that way, 95
 * or 97.5, so the buttons bring a typed value back onto the grid.
 */
export const stepFrom = (value, direction, { min, max, step }) => {
  const k = (value - min) / step;
  const onGrid = Math.abs(k - Math.round(k)) < 1e-6;
  const next = onGrid
    ? Math.round(k) + direction
    : direction > 0
      ? Math.ceil(k)
      : Math.floor(k);
  return snapToStep(min + next * step, { min, max, step });
};

// How far past either end the strip will stretch, in steps, before it stops.
const OVERSHOOT_STEPS = 0.4;

/**
 * The continuous (unsnapped) value under the centre after dragging `dx`
 * pixels from `start`. Past the ends it stretches a little and stops.
 */
export const valueFromDrag = (start, dx, { pxPerStep, min, max, step }) => {
  const raw = start - (dx / pxPerStep) * step;
  return Math.min(max + OVERSHOOT_STEPS * step, Math.max(min - OVERSHOOT_STEPS * step, raw));
};

// How long a flick keeps gliding, in ms of travel at release speed.
export const GLIDE_MS = 220;
// Slower than this (px/ms) is a placed release, not a flick.
export const FLICK_SPEED = 0.35;

/**
 * Where a release settles: the nearest step, or, after a flick, further along
 * in the direction of travel. `velocity` is in px/ms (positive = rightwards).
 */
export const releaseTarget = (raw, velocity, { pxPerStep, min, max, step, momentum }) => {
  const glide = momentum && Math.abs(velocity) > FLICK_SPEED ? velocity * GLIDE_MS : 0;
  return snapToStep(raw - (glide / pxPerStep) * step, { min, max, step });
};

/** The values to draw around `value`: `reach` steps either side, within range. */
export const stripValues = (value, { min, max, step, reach = 3 }) => {
  const out = [];
  for (let k = -reach; k <= reach; k++) {
    const v = Math.round((value + k * step) * 1000) / 1000;
    if (v >= min && v <= max) out.push({ value: v, offset: k });
  }
  return out;
};

/**
 * How a stepper in seconds shows `seconds`: plain below a minute, minutes and
 * seconds from a minute up, because 312 s is hard to read and 5:12 isn't
 * (#147). `spoken` is the screen-reader text.
 */
export const secondsDisplay = (seconds) => {
  if (seconds < 60) return { text: String(seconds), unit: "s", spoken: `${seconds} s` };
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return {
    text: `${m}:${String(s).padStart(2, "0")}`,
    unit: "min",
    spoken: s ? `${m} min ${s} s` : `${m} min`,
  };
};

/**
 * Read a value typed into the stepper (#176). `shownUnit` is the unit beside
 * the box: for a stepper in seconds that reads "min", a plain number is
 * minutes (2.5 = 2:30), and "m:ss" works either way. `parseValue` converts
 * text shown through formatValue back to the stored value (km → m). Commas
 * count as decimal points. Returns null for anything that isn't a number;
 * the caller snaps and clamps.
 */
export const parseTyped = (text, { inSeconds = false, shownUnit, parseValue } = {}) => {
  const t = String(text ?? "").trim().replace(",", ".");
  if (!t) return null;
  if (inSeconds) {
    const clock = /^(\d+):(\d{1,2})$/.exec(t);
    if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  }
  if (!/^-?\d*\.?\d+$|^-?\d+\.$/.test(t)) return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  if (parseValue) return parseValue(n);
  if (inSeconds && shownUnit === "min") return n * 60;
  return n;
};
